import { atom } from "jotai";
import type { Repository } from "../api/client";

// ── Galaxy (github-account) scope atoms ──
//
// IMPORTANT: selectedAccountAtom is a downstream MIRROR of useParams().account
// (written by Layout). It exists for chrome (Sidebar avatar, GalaxySwitcher
// current selection) where threading useParams through is awkward. It must
// NEVER drive page data fetches — pages read useParams().account directly
// and pass that to fetchGalaxy* / api.* calls.
//
// The previous design seeded the atom from localStorage and let pages read
// it for fetches. That meant an atom hydration race against URL navigation
// (post-login redirect, deep link, atom not yet hydrated) silently routed
// wrong-galaxy data into a galaxy-scoped page. localStorage seeding is
// removed; the bare entry point ("/") gets a default account from
// GalaxyRedirect using accountsAtom + JWT login, both URL-deterministic.
//
// See feedback_galaxy_url_truth.

export const selectedAccountAtom = atom<string>("");

// Write-through atom called from Layout on URL change. Kept (rather than
// directly writing selectedAccountAtom) so localStorage migration callers
// have a single migration site if we ever re-add a "last visited" hint.
export const setSelectedAccountAtom = atom(null, (_get, set, account: string) => {
  set(selectedAccountAtom, account);
});

// Available github-account logins derived from JWT user + connected
// org memberships. Set by useGalaxyInit.
export const accountsAtom = atom<string[]>([]);

// All active repos across all accounts (for galaxy list derivation).
// Currently scoped to the boot galaxy by useGalaxyInit — pages that need
// the current-galaxy repo list should derive it from useParams.account.
export const allReposAtom = atom<Repository[]>([]);

// Repos filtered to the selected galaxy only (for page-level use).
//
// NOTE: this atom is fed by selectedAccountAtom which lags useParams
// during navigation. Pages that strictly need current-galaxy repos
// (e.g. Repos.tsx, Modules.tsx for filtering) should use
// `useAtomValue(allReposAtom).filter(r => r.full_name.startsWith(account + "/"))`
// with `account` from useParams, not this atom.
export const reposAtom = atom<Repository[]>((get) => {
  const all = get(allReposAtom);
  const selected = get(selectedAccountAtom);
  if (!selected) return all;
  return all.filter((r) => r.full_name.startsWith(selected + "/"));
});

// Galaxy initialization loading state
export const galaxyLoadingAtom = atom<boolean>(true);
